import {
  AptosSignInBoundFields,
  AptosSignInInput,
  createSignInMessage,
  createSignInSigningMessage,
  generateNonce,
  type SerializedAptosSignInOutput,
} from "@aptos-labs/siwa";
import {
  Ed25519Account,
  Ed25519PrivateKey,
  Ed25519PublicKey,
  Ed25519Signature,
} from "@aptos-labs/ts-sdk";
import {
  DecibelWriteDex,
  FetchError,
  type FetchOptions,
  getRequest,
  type GetRequestArgs,
  postRequest,
  type PostRequestArgs,
} from "@decibeltrade/sdk";

import { Logger } from "@/utils/logger";

// ── Session key helpers ────────────────────────────────────────────────────

export const createAccountFromSessionKey = (sessionKey: string): Ed25519Account => {
  const privateKeyArray = new Uint8Array(sessionKey.split(",").map(Number));
  const privateKey = new Ed25519PrivateKey(privateKeyArray);
  return new Ed25519Account({ privateKey });
};

// ── SIWA signing ───────────────────────────────────────────────────────────

export interface SiwaAuthHeaders {
  "X-SIWA-Output": string;
  "X-Subaccount-Address": string;
}

/**
 * Signs a SIWA message with the given session key account and returns
 * the two headers the server expects: X-SIWA-Output and X-Subaccount-Address.
 */
export function signSiwaAuthMessage(
  writeDexSdk: DecibelWriteDex,
  accountAddress: string,
  subaccountAddress: string,
  domain: string,
): SiwaAuthHeaders {
  const nonce = generateNonce();
  const now = new Date();
  const issuedAt = now.toISOString();
  const expirationTime = new Date(now.getTime() + 1000 * 60 * 5).toISOString(); // 5 minutes
  const boundInput: AptosSignInInput & AptosSignInBoundFields = {
    nonce,
    domain,
    statement: "Sign in to Decibel",
    address: accountAddress,
    chainId: "1",
    uri: `https://${domain}`,
    version: "1",
    expirationTime,
    issuedAt,
  };

  const message = createSignInMessage(boundInput);
  const signingMessage = createSignInSigningMessage(message);

  const rawSig = writeDexSdk.account.sign(signingMessage);

  // BCS-serialize for SIWA deserialization on the server
  const sigObj = new Ed25519Signature(rawSig.toUint8Array());
  const pubObj = new Ed25519PublicKey(writeDexSdk.account.publicKey.toUint8Array());

  const serializedOutput: SerializedAptosSignInOutput = {
    version: "2",
    type: "ed25519",
    publicKey: pubObj.bcsToHex().toString(),
    signature: sigObj.bcsToHex().toString(),
    input: boundInput,
  };

  return {
    "X-SIWA-Output": JSON.stringify(serializedOutput),
    "X-Subaccount-Address": subaccountAddress,
  };
}

export const generateGetAuthHeadersFunction = (
  writeDexSdk: DecibelWriteDex | undefined,
  accountAddress: string | undefined,
  subaccountAddress: string | undefined,
  domain: string,
): GetAuthHeaders => {
  if (!writeDexSdk || !accountAddress || !subaccountAddress) {
    return undefined;
  }

  return () => signSiwaAuthMessage(writeDexSdk, accountAddress, subaccountAddress, domain);
};

// ── Authenticated request wrapper ──────────────────────────────────────────

export const normalizeRequestHeaders = (
  headers: HeadersInit | undefined,
): Record<string, string> => {
  const normalized: Record<string, string> = {};

  if (!headers) return normalized;

  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      normalized[key] = value;
    });
    return normalized;
  }

  if (Array.isArray(headers)) {
    for (const [key, value] of headers) {
      normalized[key] = value;
    }
    return normalized;
  }

  Object.assign(normalized, headers);
  return normalized;
};

export type GetAuthHeaders = (() => SiwaAuthHeaders) | undefined;

/**
 * Thrown when a 401 can't be retried with SIWA because `getAuthHeaders`
 * is undefined — typically a hydration race (initial load or subaccount
 * switch). Fixed message plus `.url` property keep Sentry grouping tight.
 */
export class AuthNotReadyError extends Error {
  readonly url: string;

  constructor(url: string) {
    super("Auth not ready");
    this.name = "AuthNotReadyError";
    this.url = url;
  }
}

/** Thrown by `authenticatedRequest.get/.post`: `FetchError` or `AuthNotReadyError`. */
export type AuthRequestError = FetchError | AuthNotReadyError;

// Placeholder base lets `new URL()` resolve relative URLs without throwing;
// an absolute input URL simply supersedes it.
const pathFromUrl = (url: string): string => new URL(url, "http://_").pathname;

function authedOptions(
  baseOptions: FetchOptions | undefined,
  subaccountAddress: string | undefined,
  extra?: Record<string, string> | SiwaAuthHeaders,
): FetchOptions & { credentials: "include"; headers: Record<string, string> } {
  const subHeader: Record<string, string> = subaccountAddress
    ? { "X-Subaccount-Address": subaccountAddress }
    : {};
  return {
    ...baseOptions,
    credentials: "include",
    headers: { ...normalizeRequestHeaders(baseOptions?.headers), ...subHeader, ...extra },
  };
}

/**
 * Cookie-first authed fetch with SIWA fallback. First tries with
 * `credentials: "include"` plus `X-Subaccount-Address` (for server-side
 * drift detection). On 401, retries with SIWA headers from
 * `getAuthHeaders` — or throws `AuthNotReadyError` if none provided.
 */
export const authenticatedRequest = {
  async get<T>(
    args: GetRequestArgs<T> & {
      getAuthHeaders?: GetAuthHeaders;
      subaccountAddress?: string;
    },
  ) {
    const { getAuthHeaders, subaccountAddress, ...requestArgs } = args;
    try {
      return await getRequest({
        ...requestArgs,
        options: authedOptions(requestArgs.options, subaccountAddress),
      });
    } catch (error) {
      if (error instanceof FetchError && error.status === 401) {
        if (!getAuthHeaders) {
          Logger.warn("authenticatedRequest: no getAuthHeaders on 401", {
            path: pathFromUrl(requestArgs.url),
          });
          throw new AuthNotReadyError(requestArgs.url);
        }
        return await getRequest({
          ...requestArgs,
          options: authedOptions(requestArgs.options, subaccountAddress, getAuthHeaders()),
        });
      }
      throw error;
    }
  },

  async post<T>(
    args: PostRequestArgs<T> & {
      getAuthHeaders?: GetAuthHeaders;
      subaccountAddress?: string;
    },
  ) {
    const { getAuthHeaders, subaccountAddress, ...requestArgs } = args;
    try {
      return await postRequest({
        ...requestArgs,
        options: authedOptions(requestArgs.options, subaccountAddress),
      });
    } catch (error) {
      if (error instanceof FetchError && error.status === 401) {
        if (!getAuthHeaders) {
          Logger.warn("authenticatedRequest: no getAuthHeaders on 401", {
            path: pathFromUrl(requestArgs.url),
          });
          throw new AuthNotReadyError(requestArgs.url);
        }
        return await postRequest({
          ...requestArgs,
          options: authedOptions(requestArgs.options, subaccountAddress, getAuthHeaders()),
        });
      }
      throw error;
    }
  },
};
